import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { BillingEntity } from 'src/features/billing/utils/types';
import { BillingEntityFormSchemaValues, makeBillingEntityFormSchema } from 'src/features/billing/utils/formSchema';
import { useGetBillingEntity } from 'src/features/billing/api/useGetBillingEntity';
import { ICON_MAP } from 'src/utils/constants';
import BillingEntityForm from 'src/features/billing/components/forms/BillingEntityForm';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';

interface BillingEntityFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillingEntity?: BillingEntity;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: FormData, onSuccess: () => void, onError: (error: GeneralASError) => void) => void;
}

function BillingEntityFormModal<TData>({
    selectedBillingEntity,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: BillingEntityFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: billingEntity } = useGetBillingEntity({
        id: selectedBillingEntity?.id ?? '',
        config: { enabled: !!selectedBillingEntity?.id && isModalOpen },
    });
    const onError = useOnError();

    const previewLogoTitle = billingEntity?.financial_document_print_logo ?? '';

    const defaultValues = React.useMemo(() => {
        return {
            name: billingEntity?.name ?? undefined,
            email: billingEntity?.email ?? undefined,
            phone: billingEntity?.phone ?? undefined,
            uic: billingEntity?.uic ?? undefined,
            code: billingEntity?.code ?? undefined,
            tax_registration_number: billingEntity?.tax_registration_number ?? undefined,
            city: billingEntity?.city ?? undefined,
            country: billingEntity?.country ?? undefined,
            address: billingEntity?.address ?? undefined,
            representative: billingEntity?.representative ?? undefined,
            invoice_prefix: billingEntity?.invoice_prefix ?? undefined,
            invoice_footer: billingEntity?.invoice_footer ?? undefined,
            financial_document_print_logo: undefined,
            financial_document_print_template: billingEntity?.financial_document_print_template ?? undefined,
            financial_document_print_color: billingEntity?.financial_document_print_color ?? undefined,
        };
    }, [billingEntity]);

    const hasExistingLogo = !!billingEntity?.financial_document_print_logo;
    const resolver = React.useMemo(
        () => zodResolver(makeBillingEntityFormSchema({ hasExistingLogo })),
        [hasExistingLogo],
    );

    const form = useForm<BillingEntityFormSchemaValues>({ resolver, defaultValues });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [billingEntity, isModalOpen, defaultValues, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit: SubmitHandler<BillingEntityFormSchemaValues> = async (values) => {
        const formData = new FormData();
        const dirtyFields = form.formState.dirtyFields;

        (Object.keys(dirtyFields) as (keyof BillingEntityFormSchemaValues)[]).forEach((key) => {
            const value = values[key];

            if (value === undefined || value === null) return;

            if (value instanceof File) {
                formData.append(key, value);
                return;
            }

            if (Array.isArray(value)) {
                value.forEach((item) => {
                    formData.append(`${String(key)}[]`, String(item));
                });
                return;
            }

            formData.append(key, String(value));
        });

        mutate(
            formData,
            () => setIsModalOpen(false),
            (error) => onError(error, form.setError),
        );
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <BillingEntityForm form={form} onSubmit={onSubmit} logoTitle={previewLogoTitle} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedBillingEntity && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BillingEntityFormModal;
